import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LLM usage metering panel — admin-only view.
 *
 * Reads GET /api/admin/usage/monthly for a given month key (YYYY-MM) and
 * renders:
 *   • grand totals row (events, tokens, USD estimate)
 *   • per-feature aggregates (feature × model)
 *   • per-user aggregates (top 100 by USD)
 *   • current model routing snapshot (env-configured feature → model)
 *   • pricing table used for USD estimates
 *
 * There is NO enforcement here — this panel is read-only reporting.
 */
const formatInt = (n) => (Number(n) || 0).toLocaleString();
const formatUsd = (n) => {
  const v = Number(n) || 0;
  return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
};

export default function LlmUsagePanel() {
  const [months, setMonths] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/usage/months`, { credentials: "include" });
        if (!r.ok) throw new Error(`months ${r.status}`);
        const j = await r.json();
        setMonths(j.months || []);
        setSelected(j.current || (j.months || [])[0] || null);
      } catch (e) {
        setErr(String(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(
          `${API}/api/admin/usage/monthly?month=${encodeURIComponent(selected)}&top_users=200`,
          { credentials: "include" },
        );
        if (!r.ok) throw new Error(`monthly ${r.status}`);
        const j = await r.json();
        setData(j);
      } catch (e) {
        setErr(String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [selected]);

  const routingRows = useMemo(() => {
    if (!data?.routing) return [];
    return Object.entries(data.routing).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  return (
    <div className="space-y-6" data-testid="admin-llm-usage-panel">
      {/* Month picker + refresh */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-wider font-bold text-[#4a6984]">
          Month
        </label>
        <select
          data-testid="llm-usage-month-select"
          value={selected || ""}
          onChange={(e) => setSelected(e.target.value)}
          className="border border-[#4a6984]/20 rounded-md px-3 py-1.5 text-sm bg-white text-[#1c2d3d]"
        >
          {[selected, ...months].filter((v, i, a) => v && a.indexOf(v) === i).map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setSelected((v) => v ? `${v}` : v)}
          disabled={loading}
          data-testid="llm-usage-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        {err && <span className="text-sm text-red-600">{err}</span>}
      </div>

      {data && (
        <>
          {/* Totals */}
          <section
            data-testid="llm-usage-totals"
            className="bg-white border border-[#4a6984]/10 rounded-lg p-5"
          >
            <div className="text-xs uppercase tracking-wider font-bold text-[#4a6984] mb-3">
              {data.month} — Grand totals
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Stat label="Events" value={formatInt(data.totals.events)} />
              <Stat label="Input tok" value={formatInt(data.totals.input_tokens)} />
              <Stat label="Output tok" value={formatInt(data.totals.output_tokens)} />
              <Stat label="Cache-write tok" value={formatInt(data.totals.cache_creation_input_tokens)} />
              <Stat label="Cache-read tok" value={formatInt(data.totals.cache_read_input_tokens)} />
              <Stat label="Est. USD" value={formatUsd(data.totals.cost_usd_estimate)} accent />
            </div>
          </section>

          {/* By feature */}
          <section
            data-testid="llm-usage-by-feature"
            className="bg-white border border-[#4a6984]/10 rounded-lg overflow-hidden"
          >
            <div className="px-5 py-3 border-b border-[#4a6984]/10 text-xs uppercase tracking-wider font-bold text-[#4a6984]">
              By feature × model
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f5f7] text-[#4a6984]">
                  <tr>
                    <Th>Feature</Th>
                    <Th>Model</Th>
                    <Th className="text-right">Events</Th>
                    <Th className="text-right">Input</Th>
                    <Th className="text-right">Output</Th>
                    <Th className="text-right">Cache-write</Th>
                    <Th className="text-right">Cache-read</Th>
                    <Th className="text-right">Est. USD</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_feature.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-6 text-[#4a6984]/70">No usage recorded for {data.month}.</td></tr>
                  )}
                  {data.by_feature.map((r, i) => (
                    <tr key={i} className="border-t border-[#4a6984]/10">
                      <Td className="font-mono text-xs">{r.feature || "(unknown)"}</Td>
                      <Td className="font-mono text-xs">{r.model || "-"}</Td>
                      <Td className="text-right">{formatInt(r.events)}</Td>
                      <Td className="text-right">{formatInt(r.input_tokens)}</Td>
                      <Td className="text-right">{formatInt(r.output_tokens)}</Td>
                      <Td className="text-right">{formatInt(r.cache_creation_input_tokens)}</Td>
                      <Td className="text-right">{formatInt(r.cache_read_input_tokens)}</Td>
                      <Td className="text-right font-semibold">{formatUsd(r.cost_usd_estimate)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* By user */}
          <section
            data-testid="llm-usage-by-user"
            className="bg-white border border-[#4a6984]/10 rounded-lg overflow-hidden"
          >
            <div className="px-5 py-3 border-b border-[#4a6984]/10 text-xs uppercase tracking-wider font-bold text-[#4a6984]">
              By user × feature (top 200 by cost)
            </div>
            <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f5f7] text-[#4a6984] sticky top-0">
                  <tr>
                    <Th>User</Th>
                    <Th>Feature</Th>
                    <Th className="text-right">Events</Th>
                    <Th className="text-right">Input</Th>
                    <Th className="text-right">Output</Th>
                    <Th className="text-right">Cache-write</Th>
                    <Th className="text-right">Cache-read</Th>
                    <Th className="text-right">Est. USD</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_user.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-6 text-[#4a6984]/70">No usage recorded for {data.month}.</td></tr>
                  )}
                  {data.by_user.map((r, i) => (
                    <tr key={i} className="border-t border-[#4a6984]/10">
                      <Td>
                        <div className="text-xs">{r.email || <span className="text-[#4a6984]/60 italic">anonymous / no user</span>}</div>
                        {r.name && <div className="text-[11px] text-[#4a6984]/70">{r.name}</div>}
                      </Td>
                      <Td className="font-mono text-xs">{r.feature}</Td>
                      <Td className="text-right">{formatInt(r.events)}</Td>
                      <Td className="text-right">{formatInt(r.input_tokens)}</Td>
                      <Td className="text-right">{formatInt(r.output_tokens)}</Td>
                      <Td className="text-right">{formatInt(r.cache_creation_input_tokens)}</Td>
                      <Td className="text-right">{formatInt(r.cache_read_input_tokens)}</Td>
                      <Td className="text-right font-semibold">{formatUsd(r.cost_usd_estimate)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Routing snapshot + pricing */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section
              data-testid="llm-usage-routing"
              className="bg-white border border-[#4a6984]/10 rounded-lg overflow-hidden"
            >
              <div className="px-5 py-3 border-b border-[#4a6984]/10 text-xs uppercase tracking-wider font-bold text-[#4a6984]">
                Model routing (env-configured)
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#f4f5f7] text-[#4a6984]">
                  <tr><Th>Feature key</Th><Th>Resolved model</Th></tr>
                </thead>
                <tbody>
                  {routingRows.map(([k, v]) => (
                    <tr key={k} className="border-t border-[#4a6984]/10">
                      <Td className="font-mono text-xs">{k}</Td>
                      <Td className="font-mono text-xs">{v}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 text-[11px] text-[#4a6984]/70 border-t border-[#4a6984]/10 bg-[#f4f5f7]/40">
                Override any value with env var <code>MODEL_&lt;FEATURE_KEY_UPPER&gt;</code>, e.g.{" "}
                <code>MODEL_MAIN_GENERATOR=claude-sonnet-5</code>. Requires backend restart.
              </div>
            </section>

            <section
              data-testid="llm-usage-pricing"
              className="bg-white border border-[#4a6984]/10 rounded-lg overflow-hidden"
            >
              <div className="px-5 py-3 border-b border-[#4a6984]/10 text-xs uppercase tracking-wider font-bold text-[#4a6984]">
                Pricing used for USD estimates ($ / 1M tok)
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#f4f5f7] text-[#4a6984]">
                  <tr>
                    <Th>Tier</Th>
                    <Th className="text-right">Input</Th>
                    <Th className="text-right">Output</Th>
                    <Th className="text-right">Cache-write (1h)</Th>
                    <Th className="text-right">Cache-read</Th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.pricing || {}).map(([tier, p]) => (
                    <tr key={tier} className="border-t border-[#4a6984]/10">
                      <Td className="font-mono text-xs">{tier}</Td>
                      <Td className="text-right">${p.input.toFixed(2)}</Td>
                      <Td className="text-right">${p.output.toFixed(2)}</Td>
                      <Td className="text-right">${p.cache_write.toFixed(2)}</Td>
                      <Td className="text-right">${p.cache_read.toFixed(2)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 text-[11px] text-[#4a6984]/70 border-t border-[#4a6984]/10 bg-[#f4f5f7]/40">
                Rates verified against Anthropic list pricing. Raw token counts are the source of truth and can be re-costed if rates change.
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent = false }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-[#4a6984]/70 font-semibold">
      {label}
    </div>
    <div className={`text-lg font-bold ${accent ? "text-[#1c2d3d]" : "text-[#1c2d3d]/90"}`}>
      {value}
    </div>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold ${className}`}>
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-2 text-[#1c2d3d] ${className}`}>{children}</td>
);
